import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Image, Upload, X, Images } from 'lucide-react';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';

export default function EditorSidebar({ onAddImage, onBulkUpload, badgeCount = 12, uploadedImages = [] }) {
  const fileInputRef = React.useRef(null);
  const bulkFileInputRef = React.useRef(null);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      onAddImage({
        url: event.target.result,
        name: file.name
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  const handleBulkUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    if (files.length > badgeCount) {
      toast.warning(`You can only upload up to ${badgeCount} photos for this badge size. Only first ${badgeCount} will be used.`);
    }

    const filesToProcess = files.slice(0, badgeCount);
    const imagePromises = filesToProcess.map((file, index) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          resolve({
            url: event.target.result,
            name: file.name,
            index: index
          });
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(imagePromises).then((images) => {
      if (onBulkUpload) {
        onBulkUpload(images);
        toast.success(`${images.length} photos uploaded successfully!`);
      }
    });

    if (bulkFileInputRef.current) {
      bulkFileInputRef.current.value = '';
    }
  };

  return (
    <div className="w-80 border-r border-border bg-card flex flex-col">
      <div className="border-b border-border p-4">
        <div className="flex items-center gap-2">
          <Image className="w-5 h-5 text-primary" />
          <h2 className="font-semibold">Upload Photos</h2>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Upload up to {badgeCount} photos for your badges
        </p>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* Bulk Upload Section */}
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Images className="w-4 h-4" />
              Bulk Upload ({badgeCount} photos max)
            </h3>
            <label htmlFor="bulk-photo-upload" className="block">
              <div className="border-2 border-dashed border-primary/50 hover:border-primary bg-primary/5 rounded-xl p-6 text-center cursor-pointer transition-all hover:bg-primary/10">
                <Upload className="w-10 h-10 mx-auto mb-3 text-primary" />
                <p className="text-sm font-medium mb-1">Click to upload multiple photos</p>
                <p className="text-xs text-muted-foreground">Select up to {badgeCount} images</p>
                <p className="text-xs text-muted-foreground mt-1">PNG, JPG up to 10MB each</p>
              </div>
              <input
                ref={bulkFileInputRef}
                id="bulk-photo-upload"
                type="file"
                accept="image/*"
                multiple
                onChange={handleBulkUpload}
                className="hidden"
              />
            </label>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">or</span>
            </div>
          </div>

          {/* Single Upload Section */}
          <div>
            <h3 className="font-semibold mb-3">Single Photo Upload</h3>
            <label htmlFor="photo-upload" className="block">
              <div className="border-2 border-dashed border-border hover:border-primary rounded-xl p-6 text-center cursor-pointer transition-all">
                <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm font-medium mb-1">Click to upload</p>
                <p className="text-xs text-muted-foreground">PNG, JPG up to 10MB</p>
              </div>
              <input
                ref={fileInputRef}
                id="photo-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </label>
          </div>

          {/* Uploaded Images Preview */}
          {uploadedImages.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3">Uploaded Photos ({uploadedImages.length}/{badgeCount})</h3>
              <div className="grid grid-cols-3 gap-2">
                {uploadedImages.map((img, idx) => (
                  <div
                    key={idx}
                    className="aspect-square rounded-lg overflow-hidden border border-border relative group"
                  >
                    <img
                      src={img.url}
                      alt={img.name || `Photo ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-xs font-medium">#{idx + 1}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
